import { Grid } from "@mui/material";
import { SectionHeading } from "~/components/DetailsPage/components";
import { EmptySection } from "~/components/vulnerabilities";
import {
  AggregateScoreOrder,
  AggregateScoreOrderField,
  AggregateScoreType,
  OrderDirection,
  useGetAggregateScoresQuery,
} from "~/operations";
import { Space } from "~/lib/types";
import { FirewatchList } from "~/components/FirewatchList";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { usePaginate } from "~/components/pagination/usePaginate";

type SoftwareVulnerabilitiesProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
  findingMrn: string;
};

export const SoftwareVulnerabilities = ({
  space,
  scope,
  findingMrn,
}: SoftwareVulnerabilitiesProps) => {
  const prefix = "vulnerability";

  const paginate = usePaginate({
    pageSizes: [3, 10, 25, 50],
    prefix,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<AggregateScoreOrder>({
    baseUrl,
    defaultSort: {
      field: AggregateScoreOrderField.Rank,
      direction: OrderDirection.Asc,
    },
    validFields: [
      AggregateScoreOrderField.RiskValue,
      AggregateScoreOrderField.Rank,
      AggregateScoreOrderField.BlastRadius,
      AggregateScoreOrderField.Title,
    ],
    prefix,
  });

  const { data, loading } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: scope.mrn,
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        scoreType: AggregateScoreType.Vulnerability,
        softwareFindingMrn: findingMrn,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const vulnerabilities =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores
      : undefined;

  const edges = vulnerabilities?.edges;
  const isEmpty = !loading && (!edges || edges?.length === 0);

  const paginateProps = paginate.pageProps(vulnerabilities?.pageInfo);

  return (
    <Grid id="version-chart" size={{ xs: 12 }}>
      <SectionHeading heading="Vulnerabilities" />
      {!isEmpty ? (
        <FirewatchList
          space={space}
          scope={scope}
          pageType={AggregateScoreType.Vulnerability}
          data={edges}
          loading={loading}
          sort={sort}
          paginateProps={paginateProps}
        />
      ) : (
        <EmptySection
          id="software-vulnerabilities-empty"
          text="There are currently no known vulnerabilities for this software"
        />
      )}
    </Grid>
  );
};
